import React, { useEffect, useState } from 'react';
import { Container, Mountain } from 'lucide-react';
import { Field } from '../sv';
import api from '../../services/api';

let cachedOptions = null;

export function useBulkOptions() {
  const [options, setOptions] = useState(cachedOptions);
  useEffect(function () {
    if (cachedOptions) return;
    api.get('/bulk/options').then(function (r) { cachedOptions = r.data; setOptions(r.data); }).catch(function () {});
  }, []);
  return options;
}

export function estimateBulkPrice(options, commodity, quantityMt) {
  if (!options || !quantityMt) return 0;
  const rate = options.rates_per_ton[commodity] ?? options.rates_per_ton.default;
  const s = options.surcharges;
  const base = Math.max(rate * quantityMt, s.minimum_freight);
  return Math.round(base * (1 + s.fuel_surcharge_percent / 100) + (s.port_charges_per_ton + s.stevedoring_per_ton) * quantityMt);
}

export function suggestVesselClass(options, quantityMt) {
  if (!options) return '';
  const vc = options.vessel_classes.find(function (v) { return quantityMt <= v.max_mt; });
  return (vc || options.vessel_classes[options.vessel_classes.length - 1]).value;
}

export function bulkPayload(formData) {
  return {
    shipment_mode: formData.shipment_mode || 'container',
    bulk_cargo_type: formData.bulk_cargo_type || null,
    bulk_commodity: formData.bulk_commodity || null,
    quantity_mt: formData.quantity_mt ? Number(formData.quantity_mt) : null,
    vessel_class: formData.vessel_class || null,
    loading_terms: formData.loading_terms || null,
  };
}

export function ShipmentModeToggle({ value, onChange }) {
  const modes = [
    { key: 'container', label: 'Container', text: 'FCL or LCL in 20\' and 40\' equipment', icon: Container },
    { key: 'bulk', label: 'Bulk', text: 'Dry or break-bulk, priced per metric ton', icon: Mountain },
  ];
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="shipment-mode-toggle" role="radiogroup">
      {modes.map(function (m) {
        const active = value === m.key;
        return (
          <button key={m.key} type="button" role="radio" aria-checked={active} onClick={function () { onChange(m.key); }} data-testid={"mode-" + m.key}
            className={"text-left p-4 rounded border flex gap-3 items-start transition-colors duration-[120ms] " + (active ? 'border-ink bg-blue-100' : 'border-gray-300 hover:bg-gray-50')}>
            <m.icon className="h-5 w-5 shrink-0 mt-0.5 text-ink" strokeWidth={1.75} />
            <span><span className="block text-[15px] font-medium">{m.label}</span><span className="block text-[13px] text-gray-600 mt-0.5">{m.text}</span></span>
          </button>
        );
      })}
    </div>
  );
}

export function BulkCargoFields({ formData, onChange, options }) {
  const type = formData.bulk_cargo_type || 'dry_bulk';
  const commodities = options ? options.commodities[type] : [];
  const suggested = suggestVesselClass(options, Number(formData.quantity_mt) || 0);
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="bulk-cargo-fields">
      <Field label="Bulk cargo type">
        <select value={type} onChange={function (e) { onChange('bulk_cargo_type', e.target.value); onChange('bulk_commodity', ''); }} className="sv-input" data-testid="bulk-cargo-type">
          <option value="dry_bulk">Dry bulk</option>
          <option value="break_bulk">Break-bulk</option>
        </select>
      </Field>
      <Field label="Commodity">
        <select value={formData.bulk_commodity || ''} onChange={function (e) { onChange('bulk_commodity', e.target.value); }} className="sv-input" data-testid="bulk-commodity">
          <option value="">Select commodity</option>
          {commodities.map(function (c) { return <option key={c.value} value={c.value}>{c.label}</option>; })}
        </select>
      </Field>
      <Field label="Quantity (metric tons)" hint={options && Number(formData.quantity_mt) > 0 ? 'Suggested vessel class: ' + suggested : 'Minimum parcel 500 MT'}>
        <input type="number" min="500" step="100" value={formData.quantity_mt || ''} placeholder="30000" onChange={function (e) { onChange('quantity_mt', e.target.value); }} className="sv-input sv-mono" data-testid="bulk-quantity" />
      </Field>
      <Field label="Vessel class">
        <select value={formData.vessel_class || suggested || 'handysize'} onChange={function (e) { onChange('vessel_class', e.target.value); }} className="sv-input" data-testid="bulk-vessel-class">
          {(options ? options.vessel_classes : [{ value: 'handysize', label: 'Handysize', dwt_range: '' }]).map(function (v) { return <option key={v.value} value={v.value}>{v.label}{v.dwt_range ? ' · ' + v.dwt_range : ''}</option>; })}
        </select>
      </Field>
      <Field label="Loading terms" className="md:col-span-2">
        <select value={formData.loading_terms || 'FIOST'} onChange={function (e) { onChange('loading_terms', e.target.value); }} className="sv-input" data-testid="bulk-loading-terms">
          {(options ? options.loading_terms : [{ value: 'FIOST', label: 'FIOST' }]).map(function (t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}
        </select>
      </Field>
    </div>
  );
}

export function BulkSummary({ formData }) {
  const rows = [
    ['Shipment', 'Bulk · ' + (formData.bulk_cargo_type || 'dry_bulk').replace('_', ' ')],
    ['Commodity', (formData.bulk_commodity || '').replace(/_/g, ' ')],
    ['Quantity', Number(formData.quantity_mt || 0).toLocaleString() + ' MT'],
    ['Vessel / terms', (formData.vessel_class || 'auto') + ' · ' + (formData.loading_terms || 'FIOST')],
  ];
  return <>{rows.map(function (r) { return <div key={r[0]} className="p-4 bg-gray-50 rounded"><p className="text-sm text-gray-600">{r[0]}</p><p className="mt-0.5 capitalize">{r[1]}</p></div>; })}</>;
}

export function cargoLabel(b) {
  if (b.shipment_mode === 'bulk') return Number(b.quantity_mt || 0).toLocaleString() + ' MT ' + String(b.bulk_commodity || 'bulk').replace(/_/g, ' ');
  return b.container_count + ' × ' + b.container_type;
}
